import { createClient, type SupabaseClient } from '@supabase/supabase-js'

/**
 * The Supabase client, used for sessions only at this stage.
 *
 * Everything the app reads still goes through edge functions; supabase-js is
 * here for session handling -- storing the session, refreshing the token before
 * it lapses -- which is fiddly enough that hand-rolling it invites the subtle
 * bugs that lock people out.
 *
 * Sign-in is by emailed code, never a link (03.5). There is therefore no
 * redirect and no session ever arrives in the URL, so detectSessionInUrl is off
 * and the default (implicit) flow is used -- PKCE existed only to exchange a
 * code carried back on a redirect, which no longer happens.
 *
 * The base URL is derived from VITE_SNAPSHOT_URL rather than configured
 * separately, so the two cannot drift apart and point at different projects.
 */
const SNAPSHOT_URL: string | undefined = import.meta.env.VITE_SNAPSHOT_URL
const PUBLISHABLE_KEY: string | undefined = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY

export function supabaseBaseUrl(): string | null {
  if (!SNAPSHOT_URL) return null
  return SNAPSHOT_URL.replace(/\/functions\/v1.*$/, '')
}

export function fn(name: string): string | null {
  const base = supabaseBaseUrl()
  return base ? `${base}/functions/v1/${name}` : null
}

/**
 * Null when the app is running against the bundled sample data, which has no
 * backend and therefore no accounts. That is a legitimate mode for working on
 * the UI, so the whole auth layer has to tolerate its absence rather than crash.
 */
export const supabase: SupabaseClient | null =
  supabaseBaseUrl() && PUBLISHABLE_KEY
    ? createClient(supabaseBaseUrl()!, PUBLISHABLE_KEY, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          // No links, so nothing ever arrives in the URL to parse (03.5).
          detectSessionInUrl: false,
          storageKey: 'hustle-auth',
        },
      })
    : null

export function authConfigured(): boolean {
  return supabase !== null
}

/** Headers for calling an edge function as the signed-in user. */
export async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {}
  if (PUBLISHABLE_KEY) headers.apikey = PUBLISHABLE_KEY
  const token = (await supabase?.auth.getSession())?.data.session?.access_token
  if (token) headers.authorization = `Bearer ${token}`
  return headers
}
